import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import { Box, Typography } from "@mui/material";
import { SiteInformation, SiteInformationItem, SiteInformationItemType } from "types/pickup";

function SummaryIcon({ type }: { type: SiteInformationItemType }) {
  if (type === "success") {
    return <CheckIcon sx={{ fontSize: 18, color: "#2E7D32", flexShrink: 0, mt: "1px" }} />;
  }

  if (type === "negative") {
    return <CloseIcon sx={{ fontSize: 18, color: "text.disabled", flexShrink: 0, mt: "1px" }} />;
  }

  return <InfoIcon sx={{ fontSize: 18, color: "primary.main", flexShrink: 0, mt: "1px" }} />;
}

function SummaryText({ item }: { item: SiteInformationItem }) {
  if (item.type === "success") {
    return (
      <Box component="span" sx={{ fontWeight: 700 }}>
        {item.text}
      </Box>
    );
  }

  if (item.type !== "info") {
    return <>{item.text}</>;
  }

  const separator = item.text.indexOf(": ");
  if (separator === -1) {
    const [lead, ...rest] = item.text.split(" ");
    return (
      <>
        <Box component="span" sx={{ fontWeight: 700 }}>
          {lead}
        </Box>
        {rest.length > 0 ? ` ${rest.join(" ")}` : null}
      </>
    );
  }

  return (
    <>
      {item.text.slice(0, separator + 2)}
      <Box component="span" sx={{ fontWeight: 700 }}>
        {item.text.slice(separator + 2)}
      </Box>
    </>
  );
}

function SummaryColumn({ title, items }: { title: string; items: SiteInformationItem[] }) {
  if (items.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "text.secondary" }}
      >
        {title}
      </Typography>
      {items.map((item) => (
        <Box key={`${item.type}-${item.text}`} sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <SummaryIcon type={item.type} />
          <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
            <SummaryText item={item} />
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function SiteInformationSummary({ siteInformation }: { siteInformation?: SiteInformation | null }) {
  if (!siteInformation?.summary) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        gap: 3,
      }}
    >
      <SummaryColumn title="Building" items={siteInformation.building} />
      <SummaryColumn title="Dock & Truck" items={siteInformation.dockAndTruck} />
    </Box>
  );
}
